import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { useAiModels } from "./useAiModels";
import { useLyricGeneration } from "./useLyricGeneration";
import { api } from "../lib/api";
import { reportError } from "../lib/reportError";
import { savePendingOrder } from "../lib/orderStorage";
import { PACKAGES } from "../components/order/orderOptions";

export const useOrder = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const models = useAiModels();
  const lyric = useLyricGeneration();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(() => ({
    first_name: "", last_name: "", email: "", phone: "", occasion: "", subject: "",
    details: "", music_style: "", voice: "", ai_model: "", lyric_language: language,
    package: PACKAGES.some(item => item.id === params.get("package")) ? params.get("package") : "",
  }));
  const [orderId, setOrderId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inFlight = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    if (models.catalog) setForm(current => ({ ...current, ai_model: current.ai_model || models.catalog.default_model }));
  }, [models.catalog]);
  const setField = (name, value) => setForm(current => ({ ...current, [name]: value }));
  const set = name => event => setField(name, event.target.value);
  const isAgreement = ["business", "album"].includes(form.package);
  const pkg = PACKAGES.find(item => item.id === form.package);

  const perform = async (action, message) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    try { await action(); }
    catch (error) {
      reportError("Order action failed", error);
      if (mounted.current) setError(message);
    } finally {
      inFlight.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  const submit = () => perform(async () => {
    const payload = { ...form };
    if (!payload.ai_model) delete payload.ai_model;
    const { data } = await api.post("/orders", payload);
    savePendingOrder(data.id, form.package);
    if (!mounted.current) return;
    setOrderId(data.id);
    setStep(4);
    if (!isAgreement) void lyric.generate(data.id, form.ai_model);
  }, "Could not submit your request. Please try again.");
  const confirm = () => perform(async () => {
    if (!lyric.ready || lyric.generating) return;
    await api.post(`/orders/${orderId}/lyrics/confirm`, { lyrics: lyric.lyrics });
    if (mounted.current) setStep(5);
  }, "Could not confirm lyrics. Please try again.");
  const pay = () => perform(async () => {
    const { data } = await api.post("/payments/checkout", { order_id: orderId, origin_url: window.location.origin });
    if (mounted.current) window.location.assign(data.checkout_url);
  }, "Could not start the payment. Please try again.");
  const payByBank = () => perform(async () => {
    await api.post(`/orders/${orderId}/bank-payment`, {});
    if (mounted.current) navigate(`/payment/bank/${orderId}`);
  }, "Could not create the QR payment slip. A card payment may already be open. Please retry or contact the studio.");
  return { step, setStep, form, set, setField, pkg, isAgreement, orderId, busy, error, models, lyric,
    submit, confirm, pay, payByBank, generate: notes => lyric.generate(orderId, form.ai_model, notes) };
};